import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Typography,
  TablePagination,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  RemoveRedEye as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useUser } from "../../contexts/UserContext";
import axios from "axios";
import apiUrl from "../api";
import InvoiceForm from "../invoiceForm/InvoiceForm";
import { io } from "socket.io-client";

function InvoiceTable({ propertyId = null, propertyIds = null, onInvoiceUpdated = null }) {
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user ? user.token : null;
  
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState(-1); // -1 for descending, 1 for ascending

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return "-";
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Function to fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      let response;
      
      // If propertyIds array is provided, use the new endpoint to fetch invoices from multiple properties
      if (propertyIds && propertyIds.length > 0) {
        console.log('Fetching invoices for multiple properties:', propertyIds);
        response = await axios.post(`${apiUrl}/invoice/properties`, 
          { propertyIds: propertyIds },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            params: {
              page: page + 1,
              perPage: rowsPerPage
            }
          }
        );
      }
      // If single propertyId is provided, get invoices for that property
      else if (propertyId) {
        console.log('Fetching invoices for single property:', propertyId);
        response = await axios.get(`${apiUrl}/invoice/property/${propertyId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            page: page + 1,
            perPage: rowsPerPage
          }
        });
      }
      // Otherwise, get all invoices
      else {
        console.log('Fetching all invoices');
        response = await axios.get(`${apiUrl}/invoice`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            page: page + 1,
            perPage: rowsPerPage
          }
        });
      }

      console.log('Invoice API response:', response.data);
      
      // Filter invoices on the client side until backend filtering is implemented
      let filteredInvoices = response.data.invoices || [];
      
      if (filterInvoiceNumber) {
        filteredInvoices = filteredInvoices.filter(invoice => 
          invoice.invoice_number.toLowerCase().includes(filterInvoiceNumber.toLowerCase())
        );
      }
      
      if (filterStatus) {
        filteredInvoices = filteredInvoices.filter(invoice => 
          (filterStatus === 'paid' && invoice.paid) || 
          (filterStatus === 'unpaid' && !invoice.paid)
        );
      }

      setInvoices(filteredInvoices);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoices when component mounts or when filters/pagination changes
  useEffect(() => {
    if (token) {
      fetchInvoices();
    }
  }, [page, rowsPerPage, filterInvoiceNumber, filterStatus, sortField, sortOrder, propertyId, propertyIds, token]);

  // Socket connection for real-time invoice updates
  useEffect(() => {
    const socket = io(apiUrl);

    // Listen for invoice updates
    socket.on("invoice", (data) => {
      if (data.action === "create") {
        const invoice = data.invoice;
        // Check if the invoice belongs to our filtered properties
        if (propertyId && invoice.property && invoice.property._id === propertyId) {
          setInvoices((prev) => [invoice, ...prev]);
          setTotalItems((prev) => prev + 1);
          // Trigger callback if provided
          if (onInvoiceUpdated) {
            onInvoiceUpdated(invoice);
          }
        } else if (propertyId && invoice.properties && invoice.properties.some(prop => 
          (typeof prop === 'object' ? prop._id : prop) === propertyId
        )) {
          setInvoices((prev) => [invoice, ...prev]);
          setTotalItems((prev) => prev + 1);
          // Trigger callback if provided
          if (onInvoiceUpdated) {
            onInvoiceUpdated(invoice);
          }
        } else if (propertyIds && invoice.property && propertyIds.includes(invoice.property._id)) {
          setInvoices((prev) => [invoice, ...prev]);
          setTotalItems((prev) => prev + 1);
          // Trigger callback if provided
          if (onInvoiceUpdated) {
            onInvoiceUpdated(invoice);
          }
        } else if (propertyIds && invoice.properties && invoice.properties.some(prop => 
          propertyIds.includes(typeof prop === 'object' ? prop._id : prop)
        )) {
          setInvoices((prev) => [invoice, ...prev]);
          setTotalItems((prev) => prev + 1);
          // Trigger callback if provided
          if (onInvoiceUpdated) {
            onInvoiceUpdated(invoice);
          }
        } else if (!propertyId && !propertyIds) {
          setInvoices((prev) => [invoice, ...prev]);
          setTotalItems((prev) => prev + 1);
          // Trigger callback if provided
          if (onInvoiceUpdated) {
            onInvoiceUpdated(invoice);
          }
        }

      } else if (data.action === "update") {
        const updatedInvoice = data.invoice;
        setInvoices((prev) =>
          prev.map((invoice) =>
            invoice._id === updatedInvoice._id ? updatedInvoice : invoice
          )
        );
        // Trigger callback if provided
        if (onInvoiceUpdated) {
          onInvoiceUpdated(updatedInvoice);
        }
  
      } else if (data.action === "delete") {
        const deletedInvoiceId = data.invoiceId;
        setInvoices((prev) =>
          prev.filter((invoice) => invoice._id !== deletedInvoiceId)
        );
        setTotalItems((prev) => prev - 1);
        // Trigger callback if provided
        if (onInvoiceUpdated) {
          onInvoiceUpdated({ _id: deletedInvoiceId, deleted: true });
        }
   
      }
    });

    // Cleanup socket listeners on component unmount
    return () => {
      socket.off("invoice");
      socket.disconnect();
    };
  }, [propertyId, propertyIds, onInvoiceUpdated]);

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 1 ? -1 : 1);
    } else {
      setSortField(field);
      setSortOrder(-1);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle view invoice
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsEditing(false);
    setOpenDialog(true);
  };

  // Handle edit invoice
  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsEditing(true);
    setOpenDialog(true);
  };

  // Handle delete invoice
  const handleDeleteInvoice = async (invoice) => {
    if (window.confirm(`Are you sure you want to delete invoice #${invoice.invoice_number}?`)) {
      try {
        await axios.delete(`${apiUrl}/invoice/${invoice._id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        fetchInvoices(); // Refresh the list
        if (onInvoiceUpdated) {
          onInvoiceUpdated();
        }
      } catch (error) {
        console.error("Error deleting invoice:", error);
        alert("Failed to delete invoice. Please try again.");
      }
    }
  };

  // Handle mark as paid
  const handleMarkAsPaid = async (invoice) => {
    try {
      await axios.put(`${apiUrl}/invoice/mark-paid/${invoice._id}`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchInvoices(); // Refresh the list
      if (onInvoiceUpdated) {
        onInvoiceUpdated();
      }
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      alert("Failed to update invoice status. Please try again.");
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedInvoice(null);
    fetchInvoices(); // Refresh the list after any potential changes
    if (onInvoiceUpdated) {
      onInvoiceUpdated();
    }
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilterInvoiceNumber("");
    setFilterStatus("");
  };

  return (
    <div style={{ padding: "10px" }}>
      {!propertyId && (
        <Typography variant="h5" gutterBottom>
          Invoices
        </Typography>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          label="Invoice Number"
          variant="outlined"
          size="small"
          value={filterInvoiceNumber}
          onChange={(e) => setFilterInvoiceNumber(e.target.value)}
          sx={{ minWidth: "200px" }}
        />

        <FormControl variant="outlined" size="small" sx={{ minWidth: "200px" }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="unpaid">Unpaid</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={handleResetFilters}
          sx={{ height: "40px" }}
        >
          Reset Filters
        </Button>
      </Box>

      {/* Invoices Table */}
      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "invoice_number"}
                  direction={sortField === "invoice_number" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("invoice_number")}
                >
                  Invoice #
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "property"}
                  direction={sortField === "property" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("property")}
                >
                  Property
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "created_at"}
                  direction={sortField === "created_at" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("created_at")}
                >
                  Created Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "amount"}
                  direction={sortField === "amount" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("amount")}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "remaining_amount"}
                  direction={sortField === "remaining_amount" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("remaining_amount")}
                >
                  Remaining
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "charges"}
                  direction={sortField === "charges" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("charges")}
                >
                  # Charges
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "paid"}
                  direction={sortField === "paid" && sortOrder === 1 ? "asc" : "desc"}
                  onClick={() => handleSortChange("paid")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice._id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex" }}>
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleViewInvoice(invoice)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {user.permissions?.canEditInvoice !== false && (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditInvoice(invoice)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* {user.permissions?.canDeleteInvoice !== false && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteInvoice(invoice)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!invoice.paid && (
                        <Tooltip title="Mark as Paid">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleMarkAsPaid(invoice)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Print">
                        <IconButton size="small">
                          <PrintIcon />
                        </IconButton>
                      </Tooltip> */}
                    </Box>
                  </TableCell>
                  <TableCell>{invoice.invoice_number}</TableCell>
                  <TableCell>
                    {invoice.properties && invoice.properties.length > 1 ? (
                      <Box>
                        {invoice.properties.map((prop, index) => (
                          <Typography key={prop._id || index} variant="body2">
                            {typeof prop === 'object' ? prop.name : `Property ID: ${prop}`}
                            {index < invoice.properties.length - 1 && ', '}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      typeof invoice.property === 'object' ? invoice.property.name : 
                      (invoice.property ? `Property ID: ${invoice.property}` : 
                      (invoice.properties && invoice.properties.length > 0 ? 
                        (typeof invoice.properties[0] === 'object' ? invoice.properties[0].name : `Property ID: ${invoice.properties[0]}`) 
                        : "-"))
                    )}
                  </TableCell>
                  <TableCell>{formatDate(invoice.created_at)}</TableCell>
                  <TableCell>{formatCurrency(invoice.amount)}</TableCell>
                  <TableCell>{formatCurrency(invoice.remaining_amount)}</TableCell>
                  <TableCell>{invoice.charges?.length || 0}</TableCell>
                  <TableCell>
                    <Chip
                      label={invoice.paid ? "Paid" : "Unpaid"}
                      color={invoice.paid ? "success" : "warning"}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      {/* Invoice Dialog */}
      {selectedInvoice && (
        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {isEditing ? "Edit Invoice" : "View Invoice"} #{selectedInvoice.invoice_number}
          </DialogTitle>
          <DialogContent>
            <InvoiceForm
              amount={selectedInvoice.amount}
              remaining_amount={selectedInvoice.remaining_amount}
              paid={selectedInvoice.paid}
              invoice_number={selectedInvoice.invoice_number}
              property={typeof selectedInvoice.property === 'object' ? 
                { value: selectedInvoice.property._id, label: selectedInvoice.property.name } :
                selectedInvoice.property ? 
                  { value: selectedInvoice.property, label: 'Property ID: ' + selectedInvoice.property } : 
                  null
              }
              properties={selectedInvoice.properties && selectedInvoice.properties.length > 0 ?
                selectedInvoice.properties.map(prop => ({
                  value: typeof prop === 'object' ? prop._id : prop,
                  label: typeof prop === 'object' ? prop.name : 'Property ID: ' + prop
                })) : []
              }
              charges={selectedInvoice.charges}
              readOnly={!isEditing}
              onSuccess={handleDialogClose}
            />
          </DialogContent>
          <DialogActions>
            {!isEditing && (
              <>
                <Button onClick={handleDialogClose}>Close</Button>
                {user.permissions?.canEditInvoice !== false && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    variant="contained"
                    color="primary"
                    startIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

export default InvoiceTable;